import React, { useEffect, useState } from 'react'
import { gql, useLazyQuery } from '@apollo/client'
import { DateTime } from 'luxon'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import { Box, Grid, InputAdornment } from '@material-ui/core'
import { DatePicker, KeyboardTimePicker } from '@material-ui/pickers'
import EventIcon from '@material-ui/icons/Event'
import Typography from '@material-ui/core/Typography'
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined'
import Divider from '@material-ui/core/Divider'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import { useAuth } from 'lib/auth'
import Dialog from 'components/dialog'
import { useStyles } from './styles'
import ConfirmModifySessionDialog from './confirm-modify-session-dialog'
import CustomLuxonUtils from 'lib/CustomLuxonUtils'
import TimeIcon from '@material-ui/icons/AccessTime'
import isValid from 'date-fns/isValid'

const COACH_SCHEDULE_AVAILABILITY = gql`
  query coachScheduleAvailability($id: ID!, $timespan: DateTimeRangeInput!) {
    coach(id: $id) {
      scheduleAvailability(timespan: $timespan) {
        isAvailable
        warnings
      }
    }
  }
`

const ModifySessionDialog = ({ trigger, booking, onSuccess }) => {
  const { lessonType, startDateTime } = booking
  const classes = useStyles()
  const { user } = useAuth()
  const [startDate, setStartDate] = useState(new Date(startDateTime))
  const [startTime, setStartTime] = useState(new Date(startDateTime))
  const [warningMessage, setWarningMessage] = useState('')
  const [isAvailableTime, setIsAvailableTime] = useState(false)
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'))

  const [checkAvailability, { data }] = useLazyQuery(
    COACH_SCHEDULE_AVAILABILITY,
  )

  useEffect(() => {
    const warningMessages = data?.coach.scheduleAvailability.warnings
    if (warningMessages && warningMessages.length > 0) {
      setWarningMessage(warningMessages[0])
    } else setWarningMessage('')
    setIsAvailableTime(!!data?.coach.scheduleAvailability.isAvailable)
  }, [data])

  const handleTimeChange = val => {
    if (!isValid(val)) return
    const minutes = val.getMinutes()
    const roundedMinutes = Math.floor(minutes / 5) * 5
    val.setMinutes(roundedMinutes)

    setStartTime(val)
    const startDateTime = new CustomLuxonUtils().mergeDateAndTime(
      DateTime.fromJSDate(startDate).toUTC(),
      DateTime.fromJSDate(val).toUTC(),
    )
    checkAvailability({
      variables: {
        id: user?.coach?.id,
        timespan: {
          from: startDateTime,
          to: startDateTime.plus({
            minutes: lessonType.durationInMinutes,
          }),
        },
      },
    })
  }

  const handleDateChange = val => {
    setStartDate(val)
    const startDateTime =
      startTime &&
      new CustomLuxonUtils().mergeDateAndTime(
        DateTime.fromJSDate(val).toUTC(),
        DateTime.fromJSDate(startTime).toUTC(),
      )
    startTime &&
      checkAvailability({
        variables: {
          id: user?.coach?.id,
          timespan: {
            from: startDateTime,
            to: startDateTime.plus({
              minutes: lessonType.durationInMinutes,
            }),
          },
        },
      })
  }

  return (
    <Dialog
      trigger={trigger}
      maxWidth="xs"
      showCloseButton
      fullScreen={isMobile}
    >
      {({ closeDialog }) => {
        const onConfirmModifySession = () => {
          closeDialog()
          onSuccess && onSuccess()
        }
        return (
          <>
            <DialogTitle>Modify session</DialogTitle>
            <Divider />
            <DialogContent>
              <Grid container>
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    gutterBottom
                    style={{ margin: '16px 0px' }}
                  >
                    Select the date and time you would like to modify this
                    session to.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box mb={2}>
                    <Typography variant="subtitle1" gutterBottom>
                      Date
                    </Typography>
                    <DatePicker
                      id="start-date"
                      value={startDate}
                      format="MMM dd, yyyy"
                      onChange={handleDateChange}
                      label="Select date"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment>
                            <EventIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Start time
                  </Typography>
                  <Box mb={2}>
                    <KeyboardTimePicker
                      id="start-time"
                      label="Select time"
                      minutesStep={5}
                      mask="__:__ _M"
                      keyboardIcon={<TimeIcon />}
                      value={startTime}
                      onChange={handleTimeChange}
                      InputProps={{
                        classes: {
                          adornedEnd: classes.adornedEnd,
                        },
                      }}
                    />
                  </Box>
                </Grid>

                {warningMessage ? (
                  <Grid item container xs={12} className={classes.warnWrapper}>
                    <Grid item container xs={2} sm={1}>
                      <ReportProblemOutlinedIcon />
                    </Grid>
                    <Grid item xs={10}>
                      <Box>
                        <Typography variant="body1" gutterBottom>
                          {warningMessage}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                ) : null}
              </Grid>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              <Button color="primary" onClick={closeDialog}>
                Cancel
              </Button>
              <ConfirmModifySessionDialog
                trigger={
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={!(startTime && startDate) || !isAvailableTime}
                  >
                    Modify
                  </Button>
                }
                startDate={startDate}
                startTime={startTime}
                bookingId={booking?.id}
                onConfirmModifySession={onConfirmModifySession}
              />
            </DialogActions>
          </>
        )
      }}
    </Dialog>
  )
}

export default ModifySessionDialog
